import * as Yup from 'yup';
import Swal from 'sweetalert2';

export const PropertyDetailsSchema = Yup.object().shape({
  property_id: Yup.string().required('Property selection is required'),
  building_name: Yup.string().required('Building name is required').trim(),
});

export const SnowIceRemovalLogValidationSchema = Yup.object().shape({
  is_na: Yup.boolean().default(false),

  date: Yup.date()
    .required('Date is required')
    .typeError('Please enter a valid date'),

  time_started: Yup.string()
    .when('is_na', {
      is: false,
      then: (schema) =>
        schema
          .matches(
            /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
            'Start time must be in HH:MM format (24-hour)'
          )
          .required('Start time is required'),
      otherwise: (schema) => schema.nullable().default(null),
    }),

  time_completed: Yup.string()
    .when('is_na', {
      is: false,
      then: (schema) =>
        schema
          .matches(
            /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
            'Completion time must be in HH:MM format (24-hour)'
          )
          .required('Completion time is required'),
      otherwise: (schema) => schema.nullable().default(null),
    })
    .test(
      'is-after-start',
      'Completion time must be after start time',
      function (value) {
        const { time_started, is_na } = this.parent;
        if (is_na) return true;
        if (!time_started || !value) return true;
        return time_started < value;
      }
    ),

  tasks: Yup.object().shape({
    sidewalks: Yup.boolean().default(false),
    all_entrances: Yup.boolean().default(false),
    stairs: Yup.boolean().default(false),
    ramp: Yup.boolean().default(false),
    amenity: Yup.boolean().default(false),
    others: Yup.boolean().default(false),
  }).test({
    name: 'at-least-one-task',
    skipAbsent: true,
    test: function (tasks) {
      if (this.parent.is_na) return true;
      if (!tasks || !Object.values(tasks).some(v => v === true)) {
        return this.createError({
          message: 'At least one task must be selected',
          path: 'tasks',
        });
      }
      return true;
    },
  }),

  actions_performed: Yup.array()
    .of(Yup.string().trim())
    .test({
      name: 'actions-required',
      test: function (value) {
        if (this.parent.is_na) return true;
        if (!value || value.length < 1) {
          return this.createError({
            message: 'At least one action must be selected',
            path: 'actions_performed',
          });
        }
        return true;
      },
    })
    .nullable(),

  temp_and_conditions: Yup.string()
    .trim()
    .nullable(),

  attachments: Yup.array()
    .of(Yup.string().trim())
    .test({
      name: 'has-attachments',
      test: function (value) {
        if (this.parent.is_na) return true;
        const uploadedCount = value?.length || 0;
        const pendingCount = this.options.context?.pendingFilesCount || 0;
        if ((uploadedCount + pendingCount) < 1) {
          return this.createError({
            message: 'At least one Attachment must be uploaded',
            path: 'attachments',
          });
        }
        return true;
      },
    }),

  comments: Yup.string()
    .trim()
    .nullable(),

  property_details: PropertyDetailsSchema.required('Property details are required'),
});

const prettifyPath = (path) => {
  if (!path) return 'Field';

  const fieldMap = {
    date: 'Date',
    time_started: 'Start Time',
    time_completed: 'Completion Time',
    'tasks.sidewalks': 'Sidewalks',
    'tasks.all_entrances': 'All Entrances',
    'tasks.stairs': 'Stairs',
    'tasks.ramp': 'Ramp',
    'tasks.amenity': 'Amenity Areas',
    'tasks.others': 'Others',
    tasks: 'Tasks Performed',
    actions_performed: 'Actions Performed',
    temp_and_conditions: 'Temperature & Conditions',
    attachments: 'Uploaded Attachments',
    comments: 'Additional Comments',
    'property_details.property_id': 'Property',
    'property_details.building_name': 'Building Name',
    property_details: 'Property Details',
  };

  return fieldMap[path] || path
    .split('.')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).replace(/_/g, ' '))
    .join(' → ');
};

const groupErrors = (errors) => {
  const grouped = {
    General: [],
    Timing: [],
    Tasks: [],
    Actions: [],
    Property: [],
    Media: [],
    Other: [],
  };

  errors.forEach(({ path, message }) => {
    let cleanMessage = message
      .replace('is required', 'is required')
      .replace('At least one task must be selected', 'please select at least one task')
      .replace('At least one action must be selected', 'please select at least one action');

    const label = prettifyPath(path);

    if (path.includes('property_details')) {
      grouped.Property.push({ label, message: cleanMessage });
    } else if (path.includes('tasks')) {
      grouped.Tasks.push({ label, message: cleanMessage });
    } else if (path === 'actions_performed') {
      grouped.Actions.push({ label, message: cleanMessage });
    } else if (path.includes('time_') || path === 'date' || path === 'day') {
      grouped.Timing.push({ label, message: cleanMessage });
    } else if (path === 'attachments' || path === 'temp_and_conditions') {
      grouped.Media.push({ label, message: cleanMessage });
    } else if (path === 'comments') {
      grouped.Other.push({ label, message: cleanMessage });
    } else {
      grouped.General.push({ label, message: cleanMessage });
    }
  });

  return grouped;
};

export const validateStep = async (schema, values, context = {}) => {
  try {
    await schema.validate(values, { abortEarly: false, context });
    return true;
  } catch (err) {
    const errors = err.inner?.map(e => ({
      path: e.path || '',
      message: e.message,
    })) || [];

    if (!errors.length) return true;

    const groupedErrors = groupErrors(errors);

    let html = `
      <p style="margin-bottom: 18px; font-size: 16px; color: #333;">
        Please correct the following before saving:
      </p>
      <div style="text-align: left; padding: 20px; background:#fff9e6; border:2px solid #ffeb3b; border-radius:12px; max-height:420px; overflow-y:auto;">
    `;

    Object.entries(groupedErrors).forEach(([section, list]) => {
      if (list.length > 0) {
        const title = section.replace(/([A-Z])/g, ' $1').trim();
        html += `
          <h4 style="margin:16px 0 10px; font-size:15px; color:#b8860b; border-bottom:1px solid #eee; padding-bottom:4px;">
            ${title}
          </h4>
        `;
        list.forEach(({ label, message }, idx) => {
          html += `
            <div style="margin-bottom:8px; color:#444;">
              <strong>${idx + 1}. ${label}:</strong> ${message}
            </div>
          `;
        });
      }
    });

    html += `</div>`;

    await Swal.fire({
      icon: 'warning',
      title: 'Missing or Invalid Fields',
      html,
      width: 800,
      confirmButtonText: "Okay, I'll Fix Them",
      confirmButtonColor: '#34c38f',
    });

    return false;
  }
};

export const formatValidationErrors = (errors) => {
  return Object.entries(errors).map(([field, message]) => {
    const pretty = prettifyPath(field);
    return `${pretty}: ${message}`;
  });
};

export default SnowIceRemovalLogValidationSchema;